import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  TextField,
} from '@material-ui/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useFormik } from 'formik'
import { useState } from 'react'
import { addUpdateBanner } from 'src/api'
import { AddVideoImageUrl, generateUniqueFileName, ImageRatioSize } from 'src/util/helpers'
import { uploadToS3 } from 'src/util/uploadToS3'

import { FlexAlignment, StatusList, TextAlignment, VisibilityList } from '../../util/constants'
import VideoThumbnailField from '../exercise/FormTypes/Fields/VideoThumbnailField'
import DropdownMenu from '../helperComponents/DropdownMenu'
import LoadingButton from '../helperComponents/LoadingButton'
import Toaster from '../helperComponents/Toaster'
import useCourseUnitChapterState from '../lesson/useCourseUnitChapterState'

const AddEditBanner = ({ selectedRow, toggleDrawer }) => {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const mutation = useMutation({
    mutationFn: values => {
      setIsSubmitting(true)
      return addUpdateBanner(values)
    },
    onSuccess: () => {
      toggleDrawer()
      setIsSubmitting(false)
      queryClient.invalidateQueries('banners')
    },
    onError: error => {
      setIsSubmitting(false)
      setShowError(true)
      setErrorMessage(error.response.data.message)
    },
  })

  const initialValues = {
    heading: selectedRow?.heading || '',
    subtext: selectedRow?.subtext || '',
    course: selectedRow?.course?._id || '',
    sortOrder: selectedRow?.sortOrder || '',
    isActive:
      selectedRow && selectedRow.isActive === undefined
        ? true
        : selectedRow && selectedRow?.isActive,
    buttonName: selectedRow?.buttonName || '',
    buttonClickUrl: selectedRow?.buttonClickUrl || '',
    videoUrl: selectedRow?.videoUrl || '',
    imageUrl: selectedRow?.imageUrl || '',
    mobileImageUrl: selectedRow?.mobileImageUrl || '',
    imageClickUrl: selectedRow?.imageClickUrl || '',
    isVideoURL: selectedRow?.isVideoURL || false,
    buttonWidth: selectedRow?.buttonWidth || 0,
    buttonPosition: selectedRow?.buttonPosition || 'center',
    textPosition: selectedRow?.textPosition || 'center',
    isPublished:
      selectedRow && selectedRow.isPublished ? selectedRow.isPublished : false,
  }

  const { values, ...formik } = useFormik({
    initialValues: initialValues,
    onSubmit: async values => {
      if (
        values.buttonClickUrl.indexOf('http') !== -1 ||
        values.buttonClickUrl.indexOf('www') !== -1
      ) {
        setErrorMessage('Only internal URLs of Langistan can be implemented')
        setShowError(true)
      } else {
        for (let key in values) {
          if (values[key] instanceof File) {
            let file = values[key]
            const uniqueName = generateUniqueFileName(file);
            const uniqueFile = new File([file], uniqueName, { type: file.type });
            await uploadToS3(uniqueFile);
            values[key] = uniqueName;
          }
        }
        values.buttonName = values.buttonName.trim()
        setIsSubmitting(true)
        values._id = selectedRow._id
        mutation.mutate(values)
        toggleDrawer(false)
      }
    },
  })

  const { coursesList } = useCourseUnitChapterState(values)

  const onCourseChange = ({ target: { value } }) => {
    formik.setValues({ ...values, chapter: '', course: value })
  }

  const handleCancel = () => {
    toggleDrawer(false)
  }
  const onStatusChange = ({ target: { value } }) => {
    formik.setValues({ ...values, chapter: '', isActive: value })
  }
  const onAlignmentChange = ({ target: { value } }) => {
    formik.setValues({ ...values, buttonPosition: value })
  }
  const onTextAlignmentChange = ({ target: { value } }) => {
    formik.setValues({ ...values, textPosition: value })
  }
  const onThumbnailChange = ({ target: { value, files } }) => {
    formik.setValues({ ...values, imageUrl: files[0] })
  }
  const onMobileThumbnailChange = ({ target: { value, files } }) => {
    formik.setValues({ ...values, mobileImageUrl: files[0] })
  }

  const onToasterClose = () => {
    setShowError(false)
    setErrorMessage('')
  }

  return (
    <>
      <Box sx={{ m: 1 }}>
        <Container>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              {/* Left Column */}
              <Grid item xs={12} md={6}>
                {/* Heading */}
                <TextField
                  error={Boolean(formik.touched.heading && formik.errors.heading)}
                  fullWidth
                  helperText={formik.touched.heading && formik.errors.heading}
                  label="Heading"
                  margin="normal"
                  name="heading"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={values.heading}
                  variant="outlined"
                  size='small'
                />

                {/* Sub Text */}
                <TextField
                  error={Boolean(formik.touched.subtext && formik.errors.subtext)}
                  fullWidth
                  helperText={formik.touched.subtext && formik.errors.subtext}
                  label="Sub Text"
                  margin="normal"
                  inputProps={{ maxLength: 70 }}
                  name="subtext"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={values.subtext}
                  variant="outlined"
                  size='small'
                />

                {/* Text Alignment */}
                <FormControl fullWidth margin="normal">
                  <DropdownMenu
                    value={values.textPosition}
                    label="Text Alignment"
                    name="textPosition"
                    isRequired={true}
                    onChange={onTextAlignmentChange}
                    data={TextAlignment}
                  />
                </FormControl>

                {/* Language Selection */}
                <FormControl fullWidth margin="normal">
                  <DropdownMenu
                    value={values.course}
                    label="Language"
                    name="course"
                    isRequired={true}
                    onChange={onCourseChange}
                    data={coursesList}
                  />
                </FormControl>

                {/* Website Banner Upload */}
                <Grid item xs={12} mt={2}>
                  <VideoThumbnailField
                    imageUrl={values?.imageUrl}
                    onChange={onThumbnailChange}
                    label="Upload Website Banner"
                  />
                  <ImageRatioSize ratio="5:1" minWidth="1550px" size="500kb" />
                </Grid>

                {/* Mobile Banner Upload */}
                <Grid item xs={12} mt={2}>
                  <VideoThumbnailField
                    imageUrl={values?.mobileImageUrl}
                    onChange={onMobileThumbnailChange}
                    label="Upload Mobile Banner"
                    id="contained-button-mobile-file"
                  />
                  <ImageRatioSize ratio="9∶7" minWidth="540px" size="500kb" />
                </Grid>

                {/* Video URL */}
                <Box sx={{ width: "100%" }}>
                  <TextField
                    error={Boolean(formik.touched.videoUrl && formik.errors.videoUrl)}
                    fullWidth
                    helperText={formik.touched.videoUrl && formik.errors.videoUrl}
                    label="Video URL"
                    type="url"
                    placeholder="Video Link"
                    margin="normal"
                    name="videoUrl"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={values.videoUrl}
                    variant="outlined"
                    size='small'
                  />
                  <AddVideoImageUrl text="Add full YouTube or video URL." />
                </Box>
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} md={6}>
                {/* Button Name */}
                <TextField
                  error={Boolean(formik.touched.buttonName && formik.errors.buttonName)}
                  fullWidth
                  helperText={formik.touched.buttonName && formik.errors.buttonName}
                  label="CTA Button/CTA Button Text"
                  placeholder="CTA Button/CTA Button Text"
                  margin="normal"
                  name="buttonName"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={values.buttonName}
                  variant="outlined"
                  size='small'
                />

                {/* Button Click URL */}
                <Box sx={{ width: "100%", mb: 2 }}>
                  <TextField
                    error={Boolean(formik.touched.buttonClickUrl && formik.errors.buttonClickUrl)}
                    fullWidth helperText={formik.touched.buttonClickUrl && formik.errors.buttonClickUrl}
                    label="Destination URL"
                    placeholder="/coursedetails/63aec2939970ebfbde6cc3f7"
                    margin="normal" name="buttonClickUrl"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={values.buttonClickUrl}
                    variant="outlined"
                    size='small' />

                  <AddVideoImageUrl text="Add only the segment after the main URL. For example: /coursedetails/639b2dbcd5b735705b4321b9" />
                </Box>

                {/* Button Width */}
                <TextField
                  fullWidth
                  label="CTA Button Width"
                  margin="normal"
                  inputProps={{ min: 0 }}
                  name="buttonWidth"
                  type="number"
                  onChange={formik.handleChange}
                  value={values.buttonWidth}
                  variant="outlined"
                  size='small'
                />

                {/* Button Alignment */}
                <FormControl fullWidth margin="normal">
                  <DropdownMenu
                    value={values.buttonPosition}
                    label="CTA Button Alignment"
                    name="buttonPosition"
                    isRequired={true}
                    onChange={onAlignmentChange}
                    data={FlexAlignment}
                  />
                </FormControl>

                {/* Sort Order */}
                <TextField
                  fullWidth
                  label="Sort Order"
                  margin="normal"
                  inputProps={{ min: 0 }}
                  name="sortOrder"
                  type="number"
                  onChange={formik.handleChange}
                  value={values.sortOrder}
                  variant="outlined"
                  size='small'
                />

                {/* Status */}
                <FormControl fullWidth margin="normal">
                  <DropdownMenu
                    value={values.isActive}
                    label="Status"
                    name="isActive"
                    isRequired={true}
                    onChange={onStatusChange}
                    data={StatusList}
                  />
                </FormControl>

                {/* Is Published */}
                <FormControl fullWidth margin="normal">
                  <DropdownMenu
                    value={values.isPublished}
                    label="Visibility"
                    name="isPublished"
                    isRequired={true}
                    onChange={formik.handleChange}
                    data={VisibilityList}
                  />
                </FormControl>
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12} md={6}>
                <Button
                  color="primary"
                  onClick={handleCancel}
                  fullWidth
                  size="large"
                  type="button"
                  variant="outlined"
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <LoadingButton
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  name="Save"
                >
                </LoadingButton>
              </Grid>

            </Grid>
          </form>

        </Container>
      </Box>
      {showError && (
        <Toaster msg={errorMessage} severity="error" onClose={onToasterClose} />
      )}
    </>
  )
}

export default AddEditBanner
